/**
 * ProposalsTab Component
 * TASK-395 + TASK-396: Preparation Screen — Proposals Tab
 *
 * Uses the existing PortfolioComposer (full simulation modal) and
 * WhatIfSimulator (scenario cards) from the portfolio module.
 * Saved proposals are stored in proposalStore.
 *
 * Pattern follows RiskTab.tsx (lines 425-524).
 */

import React, { useState, useEffect } from 'react';
import { Plus, TrendingUp } from 'lucide-react';
import {
  useProposalStore,
  useProposals,
  getProposalBalance,
  type Proposal,
} from '../../../stores/proposalStore';
import { ImpactMetrics, computeProposalImpact, mapBackendProposal } from './proposals';
import type { BackendProposal } from './proposals/mapBackendProposal';
import PortfolioComposer from '../../portfolio/PortfolioComposer';
import WhatIfSimulator from '../../portfolio/molecules/WhatIfSimulator';
import { portfolioApiService } from '../../../services/api/portfolioApiService';

// ============================================================================
// Types
// ============================================================================

interface ProposalsTabProps {
  meetingId: string;
  clientId: string;
}

// ============================================================================
// Component
// ============================================================================

const ProposalsTab: React.FC<ProposalsTabProps> = ({ meetingId: _meetingId, clientId }) => {
  const proposals = useProposals();
  const { removeProposal, setProposals } = useProposalStore();
  const composerPrefill = useProposalStore((s) => s.composerPrefill);
  const setComposerPrefill = useProposalStore((s) => s.setComposerPrefill);
  const [showComposer, setShowComposer] = useState(false);

  // TASK-396: an AI-suggested proposal (proposal_action) opens the composer pre-filled.
  useEffect(() => {
    if (composerPrefill) setShowComposer(true);
  }, [composerPrefill]);

  const closeComposer = () => {
    setShowComposer(false);
    setComposerPrefill(null);
  };

  // Human-initiated: open a fresh composer, discarding any stale AI prefill.
  const openEmptyComposer = () => {
    setComposerPrefill(null);
    setShowComposer(true);
  };

  const handleComposerSaved = async () => {
    // PortfolioComposer persists to the backend; refetch the truth (no stub entry) so
    // the saved trades + ImpactMetrics render, and AI/human proposals are treated alike.
    try {
      const response = await portfolioApiService.getProposals(clientId);
      const backend = (response?.proposals ?? []) as BackendProposal[];
      setProposals(backend.map(mapBackendProposal));
    } catch (err) {
      console.error('Failed to refresh proposals after save:', err);
    }
    closeComposer();
  };

  return (
    <div className="h-full overflow-y-auto">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 bg-white sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Investment Proposals</h2>
            <p className="text-sm text-gray-500">
              {proposals.length === 0
                ? 'Use the portfolio simulator to build proposals'
                : `${proposals.length} proposal${proposals.length !== 1 ? 's' : ''}`}
            </p>
          </div>
          <button
            onClick={openEmptyComposer}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Proposal
          </button>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* What-If Simulator — scenario cards + "Build Custom" button */}
        <WhatIfSimulator
          onOpenComposer={openEmptyComposer}
          currency="CHF"
        />

        {/* Saved proposals list */}
        {proposals.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Saved Proposals</h3>
            <div className="space-y-3">
              {proposals.map((proposal) => (
                <SavedProposalCard
                  key={proposal.id}
                  proposal={proposal}
                  onRemove={() => removeProposal(proposal.id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Empty state when no proposals and no simulator */}
        {proposals.length === 0 && (
          <div className="text-center py-8">
            <TrendingUp className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-xs text-gray-500 max-w-sm mx-auto">
              Click "New Proposal" or "Build Custom Scenario" above to open the portfolio simulator.
              Add buy/sell transactions, run simulations, and save proposals.
            </p>
          </div>
        )}
      </div>

      {/* Portfolio Composer Modal — pre-filled when opened from an AI proposal */}
      {clientId && showComposer && (
        <PortfolioComposer
          clientId={clientId}
          isOpen={showComposer}
          onClose={closeComposer}
          onSaveSuccess={handleComposerSaved}
          initialTransactions={composerPrefill?.transactions}
          initialProposalName={composerPrefill?.title}
          initialProposalNotes={composerPrefill?.notes}
          verifiedConstraints={composerPrefill?.verifiedConstraints}
          riskDeltas={composerPrefill?.riskDeltas}
        />
      )}
    </div>
  );
};

// ============================================================================
// Saved Proposal Card (lightweight display of stored proposals)
// ============================================================================

const SavedProposalCard: React.FC<{
  proposal: Proposal;
  onRemove: () => void;
}> = ({ proposal, onRemove }) => {
  const balance = getProposalBalance(proposal);
  const snapshot = useProposalStore((s) => s.portfolioSnapshot);
  const sellCount = proposal.trades.filter((t) => t.action === 'sell').length;
  const buyCount = proposal.trades.filter((t) => t.action === 'buy').length;
  const impactMetrics = snapshot && proposal.trades.length > 0
    ? computeProposalImpact(proposal, snapshot)
    : null;

  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
      <div className="flex items-start justify-between mb-2">
        <div>
          <h4 className="text-sm font-semibold text-gray-800">{proposal.title}</h4>
          <div className="flex items-center gap-2 mt-1">
            <span className="px-2 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-600 rounded-full capitalize">
              {proposal.category}
            </span>
            <span className="px-2 py-0.5 text-[10px] font-medium bg-blue-50 text-blue-600 rounded-full">
              {proposal.status}
            </span>
            <span className="text-[10px] text-gray-400">
              by {proposal.createdBy === 'ai' ? 'AI' : 'you'}
            </span>
          </div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="text-xs text-gray-400 hover:text-red-500 transition-colors"
        >
          Remove
        </button>
      </div>

      {proposal.trades.length > 0 ? (
        <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
          <span className="text-red-600">{sellCount} sell</span>
          <span className="text-green-600">{buyCount} buy</span>
          <span className={balance.isBalanced ? 'text-green-600' : 'text-amber-600'}>
            Balance: {balance.isBalanced ? 'Matched' : `${balance.balance > 0 ? '+' : ''}${balance.balance.toLocaleString()}`}
          </span>
        </div>
      ) : (
        <p className="text-xs text-gray-400 italic">No trades — created from simulator</p>
      )}

      {impactMetrics && <ImpactMetrics metrics={impactMetrics} />}
    </div>
  );
};

export default ProposalsTab;
